import AddIcon from '@mui/icons-material/Add'
import InfoIcon from '@mui/icons-material/Info'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import TablePagination from '@mui/material/TablePagination'
import TextField from '@mui/material/TextField'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { lighten } from '@mui/material/styles'

type Props = {
    canAddProducts: boolean
    rowsPerPage: number
    numSelected: number
    loading: boolean
    page: number
    count: number
    bulkDiscountPercentage: number
    disabledBulkEdit: boolean
    onChangePage: (page: number) => void
    onAddProducts: () => void
    onRemoveProducts: () => void
    onSearch: (term: string) => void
    onBulkDiscountPercentChanged: (value: number) => void
}

export default function DiscountTableToolbar({
    canAddProducts,
    numSelected,
    page,
    count,
    bulkDiscountPercentage,
    disabledBulkEdit,
    loading,
    rowsPerPage,
    onChangePage,
    onAddProducts,
    onRemoveProducts,
    onBulkDiscountPercentChanged,
    onSearch,
}: Props) {
    return (
        <Toolbar
            sx={{
                paddingRight: (theme) => theme.spacing(1),
                justifyContent: 'space-between',
                minHeight: 67,
                color: (theme) => {
                    if (numSelected) {
                        return theme.palette.mode === 'light'
                            ? theme.palette.secondary.main
                            : theme.palette.text.primary
                    }
                },
                backgroundColor: (theme) => {
                    if (numSelected) {
                        return theme.palette.mode === 'light'
                            ? lighten(theme.palette.secondary.light, 0.85)
                            : theme.palette.secondary.dark
                    }
                },
            }}
        >
            {loading && (
                <LinearProgress
                    color="secondary"
                    sx={{ position: 'absolute', left: 0, right: 0, top: 0 }}
                />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 67, gap: 2 }}>
                <Box sx={{ flex: '0 0 auto' }}>
                    {numSelected > 0 ? (
                        <Typography color="inherit" variant="subtitle1">
                            {interpolate(pgettext('Number selected', '%s selected'), [numSelected])}
                        </Typography>
                    ) : (
                        <Typography variant="subtitle1">{gettext('Discount products')}</Typography>
                    )}
                </Box>
                <TextField
                    id="search"
                    type="search"
                    variant="outlined"
                    placeholder={gettext('Search products')}
                    InputProps={{ style: { height: '38px' } }}
                    onChange={(e) => onSearch(e.target.value)}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        type="number"
                        variant="outlined"
                        value={bulkDiscountPercentage}
                        label={gettext('Bulk discount')}
                        disabled={disabledBulkEdit}
                        inputProps={{
                            style: { textAlign: 'right' },
                        }}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            style: { height: '38px' },
                        }}
                        onChange={(e) => {
                            if (e.target instanceof HTMLInputElement) {
                                onBulkDiscountPercentChanged(e.target.valueAsNumber)
                            }
                        }}
                    />
                    <Tooltip
                        placement="right"
                        title={gettext(
                            'Bulk discount will be applied to all products in this discount.'
                        )}
                    >
                        <InfoIcon fontSize="small" />
                    </Tooltip>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', color: 'text.secondary' }}>
                {numSelected > 0 ? (
                    <Button color="secondary" onClick={onRemoveProducts}>
                        {gettext('Remove from discount')}
                    </Button>
                ) : (
                    <>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<AddIcon />}
                            sx={{ height: 38, alignSelf: 'center', width: 175 }}
                            disabled={canAddProducts}
                            onClick={onAddProducts}
                        >
                            {gettext('Add products')}
                        </Button>
                        <TablePagination
                            component="div"
                            count={count}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            slotProps={{
                                actions: {
                                    previousButton: {
                                        'aria-label': gettext('Previous'),
                                    },
                                    nextButton: {
                                        'aria-label': gettext('Next'),
                                    },
                                },
                            }}
                            rowsPerPageOptions={[rowsPerPage]}
                            onPageChange={(_, newPage) => onChangePage(newPage)}
                        />
                    </>
                )}
            </Box>
        </Toolbar>
    )
}
